import React, { useContext, useState, useEffect, useMemo, ChangeEvent } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { message } from 'react-message-popup';
import { Helmet } from 'react-helmet';

import Header from "../components/Header";
import Footer from "../components/Footer";
import { RuntimeValuesContext } from '../context/RuntimeValuesContext';
import { useTranslation } from 'react-i18next';
import config from '../config/config.json';
import PayPalCheckoutButton from '../components/PayPalCheckoutButton';

function Checkout() {
    const { t } = useTranslation("global");
    const { runtimeValues, setRuntimeValues } = useContext(RuntimeValuesContext);
    const navigate = useNavigate();

    const [cartTotal, setCartTotal] = useState(0);
    const [createdOrderId, setCreatedOrderId] = useState<string | null>(null);
    const [isProcessing, setIsProcessing] = useState(false);

    const [title, setTitle] = useState<string>(`${t("pageTitle.checkout")} ${t("seo.compl")} ${runtimeValues.country} - wicaglobalgear.com`);

    useEffect(() => {
        const subTotal = runtimeValues.cart.reduce((acc, item) => acc + ((item.price ?? 0) * (item.cant ?? 0)), 0);
        setCartTotal(subTotal + runtimeValues.shippingPrice);
    }, [runtimeValues.cart, runtimeValues.shippingPrice]);

    useEffect(() => {
        setTitle(`${t("pageTitle.checkout")} ${t("seo.compl")} ${runtimeValues.country} - wicaglobalgear.com`);
        document.title = title;
    }, [runtimeValues.country, t, title]);

    const handleChangeClientInfo = (event: React.ChangeEvent<HTMLInputElement>) => {
        const { name, value } = event.target;
        setRuntimeValues({ ...runtimeValues, [name]: value });
    };

    const handlePaymentOptions = (event: ChangeEvent<HTMLInputElement>) => {
        setRuntimeValues({ ...runtimeValues, payment: event.target.value });
    };

    const validateClientInfo = () => {
        const validationErrors: string[] = [];
        if (runtimeValues.cart.length === 0) {
            validationErrors.push(t("cart.validationItems") || "Items in cart");
        }
        if (runtimeValues.clientName.trim() === "") {
            validationErrors.push(t("cart.clientName") || "Full Name");
        }
        if (runtimeValues.shippingPhoneNumber.trim().length < 10) {
            validationErrors.push(t("cart.addressPhoneNumber") || "Phone Number");
        }
        if (!/^\S+@\S+\.\S+$/.test(runtimeValues.clientEmail)) {
             validationErrors.push("a valid Email");
        }

        if (validationErrors.length > 0) {
            const validationMessage = t("cart.validationMessage") || "Please complete the following fields";
            message.error(`${validationMessage}: ${validationErrors.join(', ')}`, 4000);
            return false; // Validation failed
        }
        return true; // Validation passed
    };
    
    const handlePlaceOrder = async () => {
        if (!validateClientInfo()) {
            return;
        }

        setIsProcessing(true);

        const orderPayload = {
            ...runtimeValues,
            payment: runtimeValues.payment === 'creditCard' ? 'PayPal' : 'toBeConvened',
        };

        try {
            const response = await axios.post(`${config.backend.url}/api/orders`, {
                params: orderPayload
            });

            if (response.status === 201 && response.data.id) {
                const newOrderId = response.data.id.toString();
                console.log("Internal order created, ID:", newOrderId);

                if (runtimeValues.payment === 'creditCard') { // 'creditCard' means PayPal
                    // Order created, now show the PayPal button for payment.
                    setCreatedOrderId(newOrderId);
                } else {
                    // It's a Purchase Order, so the flow is complete. Navigate to the detail page.
                    handlePaymentSuccess(newOrderId);
                }
            } else {
                throw new Error('Backend did not return a valid order ID.');
            }
        } catch (error) {
            console.error("Error creating order:", error);
            message.error("Failed to create your order. Please try again.");
        } finally {
            setIsProcessing(false);
        }
    };
    
    const handlePaymentSuccess = (details: { internalOrderId: string; transactionId: string }) => {
        // Clear the cart and other client info from context after a successful payment.
        setRuntimeValues(prev => ({ ...prev, cart: [], clientName: "", clientEmail: "", shippingPhoneNumber: "" }));
        // Navigate to the order detail page to show the user their completed order.
        navigate(`/orderDetail?orderId=${details.internalOrderId}`);
    };

    let formatter = new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' });
    switch (runtimeValues.currency) {
        case 'COP': formatter = new Intl.NumberFormat('es-CO', { style: 'currency', currency: 'COP' }); break;
        case 'CLP': formatter = new Intl.NumberFormat('es-CL', { style: 'currency', currency: 'CLP' }); break;
        case 'PEN': formatter = new Intl.NumberFormat('es-PE', { style: 'currency', currency: 'PEN' }); break;
        case 'PYG': formatter = new Intl.NumberFormat('es-PY', { style: 'currency', currency: 'PYG' }); break;
        case 'UYU': formatter = new Intl.NumberFormat('es-UY', { style: 'currency', currency: 'UYU' }); break;
        default: formatter = new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' });
    }

    if (runtimeValues.cart.length === 0) {
        return (
            <div className="app-container">
                <Header />
                <div className="app-content" style={{ flexDirection: 'column', alignItems: 'center', textAlign: 'center', padding: '50px' }}>
                    <h1>{t("pageTitle.checkout")}</h1>
                    <p>{t("cart.cartEmpty") || "Your cart is empty."}</p>
                    <Link to="/products" className="plpButtonFocus" style={{ marginTop: '20px' }}>
                        {t("general.addMore") || "Continue Shopping"}
                    </Link>
                </div>
                <Footer />
            </div>
        );
    }

    return (
        <>
            <Helmet><title>{title}</title></Helmet>
            <div className="app-container">
                <Header />
                <div className="app-content">
                    <section className="sectionPageContent">
                        <h1>{t("pageTitle.checkout")}</h1>
                        <div className="cartContainer">
                            <div className="cartItemContainerLeft">
                                <h3>{t("checkout.clientTitle")}</h3>
                                <div className="form">
                                    <div className="form-row"><div className="input-data"><input type="text" name="clientName" value={runtimeValues.clientName} onChange={handleChangeClientInfo} required /><div className="underline"></div><label>{t("cart.clientName")}</label></div></div>
                                    <div className="form-row"><div className="input-data"><input type="text" name="shippingPhoneNumber" value={runtimeValues.shippingPhoneNumber} onChange={handleChangeClientInfo} required /><div className="underline"></div><label>{t("cart.addressPhoneNumber")}</label></div></div>
                                    <div className="form-row"><div className="input-data"><input type="email" name="clientEmail" value={runtimeValues.clientEmail} onChange={handleChangeClientInfo} required /><div className="underline"></div><label>Email</label></div></div>
                                </div>
                            </div>
                            <div className="cartItemContainerRight">
                                <h3>{t("checkout.orderSummary")}</h3>
                                <div className="cartItemContainerRightItems">
                                    <div className="cartTotalItemTitle"><span className="spanFilter">{t("cart.shipping")}:</span></div>
                                    <div className="cartTotalItemValue"><span className="spanFilter">{runtimeValues.shipping === "pickup" ? t("cart.pickUp") : `${t("cart.shipping")}: ${runtimeValues.country}/${runtimeValues.shippingCity}`}</span></div>
                                </div>
                                <div className="cartItemContainerRightItems" style={{ borderTop: '1px solid #ccc', paddingTop: '10px', marginTop: '10px' }}>
                                    <div className="cartTotalItemTitle"><span className="spanFilter" style={{fontWeight: 'bold'}}>Total:</span></div>
                                    <div className="cartTotalItemValue"><span className="spanFilter" style={{ fontWeight: 'bold' }}>{formatter.format(cartTotal)}</span></div>
                                </div>
                                
                                <div className="cartItemContainerRightItems" style={{ marginTop: '20px' }}><div className="cartTotalItemTitle"><span className="spanFilter">{t("cart.paymentMethod")}:</span></div></div>
                                <div className="cartItemContainerRightItems"><div className="cartTotalItemTitle"><ul>
                                    <li className="checkboxFilter">
                                        <input className='radio' type="radio" name="payment" value="creditCard" checked={runtimeValues.payment === "creditCard"} onChange={handlePaymentOptions} />
                                        <span className="spanItemLower">{t("cart.creditCard")}</span>
                                    </li>
                                    <li className="checkboxFilter">
                                        <input className='radio' type="radio" name="payment" value="toBeConvened" checked={runtimeValues.payment === "toBeConvened"} onChange={handlePaymentOptions} />
                                        <span className="spanItemLower">{t("cart.toBeConvened")}</span>
                                    </li>
                                </ul></div></div>
                                
                                { !createdOrderId ? (
                                    <button
                                        className="plpButtonFocus"
                                        style={{ marginTop: '20px', width: '100%' }}
                                        onClick={handlePlaceOrder}
                                        disabled={isProcessing}
                                    >
                                        {isProcessing ? t("checkout.processing") : (runtimeValues.payment === 'creditCard' ? t("checkout.continueToPayment") : t("checkout.purchaseButton"))}
                                    </button>
                                ) : (
                                    <div style={{ marginTop: '20px' }}>
                                        <p style={{textAlign: 'center', marginBottom: '10px'}}>{t("checkout.completePaymentMessage")}</p>
                                        <PayPalCheckoutButton
                                            internalOrderId={createdOrderId}
                                            currency={runtimeValues.currency}
                                            amount={cartTotal}
                                            onPaymentSuccess={handlePaymentSuccess}
                                        />
                                    </div>
                                )}
                            </div>
                        </div>
                        <div className="product-actions" style={{marginTop: '40px'}}>
                            <Link to="/cart" className="plpButton">{t("general.backCart")}</Link>
                        </div>
                    </section>
                </div>
                <Footer />
            </div>
        </>
    );
}

export default Checkout;